import { BufferAttribute, BufferGeometry } from "three";
import { unindexed } from "../geometry/unindexed.ts";

/**
 * Geometria preparada da superfície dos atores (F118): a do modelo, sem índice (a cor RGBA dos GLBs
 * vira `color` + a máscara `tint`, em `unindexed`), com tudo DIVIDIDO por referência (posição, skin,
 * cor, máscara; nenhum buffer copiado, nada recalculado). O material declara `tint` (a máscara da
 * costura); sem ela, entra uma de zeros (Float32, alinhada): o `MeshStandardMaterial` não tem valores
 * padrão de atributo, e um atributo ausente leria o último valor genérico de outro programa.
 *
 * Fase 15: sem as linhas e os nós, saíram o baricêntrico, a máscara dos cantos e o sorteio deles; a
 * marca de preparada é `userData.energy` (`isPrepared`).
 */

/** Atributos que a geometria preparada divide com a fonte (nunca descartados por ela). */
const SHARED = ["position", "skinIndex", "skinWeight", "color", "tint", "normal"] as const;

/** A geometria preparada (nova; os atributos da fonte entram por referência). Lança erro se não for de triângulos. */
export function prepareEnergyGeometry(source: BufferGeometry): BufferGeometry {
  const flat = source.index ? unindexed(source) : source;
  const position = flat.getAttribute("position");
  if (!position || position.count % 3 !== 0) throw new Error("superfície dos atores: a geometria precisa de posições em triângulos");
  const count = position.count;
  const out = new BufferGeometry();
  out.name = flat.name;
  for (const name of SHARED) {
    const attribute = flat.getAttribute(name);
    if (attribute) out.setAttribute(name, attribute);
  }
  const ownTint = !out.getAttribute("tint");
  if (ownTint) out.setAttribute("tint", new BufferAttribute(new Float32Array(count), 1));
  for (const g of flat.groups) out.addGroup(g.start, g.count, g.materialIndex);
  out.setDrawRange(flat.drawRange.start, flat.drawRange.count);
  if (flat.boundingSphere) out.boundingSphere = flat.boundingSphere.clone();
  if (flat.boundingBox) out.boundingBox = flat.boundingBox.clone();
  const energy: PreparedInfo = { ownTint, bytes: ownTint ? count * 4 : 0 };
  out.userData = { ...flat.userData, energy };
  return out;
}

/** O que a preparação guarda em `userData.energy`: se a máscara é dela e quantos bytes acrescentou. */
export interface PreparedInfo {
  ownTint: boolean;
  bytes: number;
}

/** Bytes que a preparação acrescentou (a máscara criada; 0 se não é preparada ou se a fonte já tinha). */
export function addedBytes(prepared: BufferGeometry): number {
  return (prepared.userData["energy"] as PreparedInfo | undefined)?.bytes ?? 0;
}

/**
 * A geometria já é preparada (`prepareTree`, um ator já coberto): a marca é a da preparação, não um
 * atributo. Uma fonte com `userData.energy` de outro dono não existe (o `userData` dos GLBs vem vazio).
 */
export function isPrepared(geometry: BufferGeometry): boolean {
  const info = geometry.userData["energy"] as PreparedInfo | undefined;
  return typeof info === "object" && info !== null && typeof info.ownTint === "boolean";
}

interface Entry {
  geometry: BufferGeometry;
  refs: number;
}

/**
 * Geometrias preparadas por fonte, com contagem de referências: corpos iguais dividem a mesma; a
 * última a sair descarta só o que é dela (a máscara criada), nunca os buffers divididos com a fonte
 * (que outros desenhos ainda usam). Contrato: quem segura a geometria
 * preparada fora da contagem (o clone de um ator já coberto, um lote com a peça dele) fica com ela
 * vazia depois da última devolução; clonar só antes de aplicar a superfície, ou adquirir também.
 */
export class PreparedGeometries {
  private readonly entries = new Map<string, Entry>();

  acquire(source: BufferGeometry): BufferGeometry {
    const key = source.uuid;
    let entry = this.entries.get(key);
    if (!entry) this.entries.set(key, (entry = { geometry: prepareEnergyGeometry(source), refs: 0 }));
    entry.refs++;
    return entry.geometry;
  }

  /** Devolve uma referência; na última, descarta o que é da preparação. */
  release(prepared: BufferGeometry): void {
    for (const [key, entry] of this.entries) {
      if (entry.geometry !== prepared) continue;
      if (--entry.refs > 0) return;
      this.entries.delete(key);
      disposeOwn(entry.geometry);
      return;
    }
  }

  get size(): number {
    return this.entries.size;
  }
}

/**
 * Descarta a geometria preparada sem tocar nos buffers divididos: o `dispose` do three apaga o buffer
 * de GPU de TODO atributo da geometria (e os VAOs de outra geometria com o mesmo atributo ficariam
 * apontando para um buffer apagado), então os divididos saem dela antes.
 */
function disposeOwn(prepared: BufferGeometry): void {
  const own = (prepared.userData["energy"] as PreparedInfo | undefined)?.ownTint === true;
  for (const name of SHARED) if (name !== "tint" || !own) prepared.deleteAttribute(name);
  prepared.dispose();
}

/** O cache do jogo (uma página; as geometrias dos tipos carregados vivem nele até a última referência). */
export const PREPARED = new PreparedGeometries();
